// proj4
import proj4 from 'proj4';

// turf
import { lineDistance } from '@turf/turf';

// mapbox lib
import * as CommonSelectors from '@/vue3/mapbox/lib/commonSelectors';
import * as Constants from './constants';
import isEventAtCoordinates from '@/vue3/mapbox/lib/isEventAtCoordinates';
import doubleClickZoom from '@/vue3/mapbox/lib/doubleClickZoom';

import { useStore } from '@/store';

const MeasureLineStringMode: any = {};

const anyArr: any[] = [];
let measurement = {
    metricTotal: 0,
    metricMeasurement: anyArr,
    metricUnits: ``,
};

let previousMeasureTotal = 0;
let lastCompletedLineLength = 0;
let currentLineLength = 0;
let tempState: any;

const store = useStore();

let isMeters;

const resetLineMeasurements = () => {
    measurement = {
        metricTotal: 0,
        metricMeasurement: anyArr,
        metricUnits: ``,
    };
    previousMeasureTotal = 0;
    lastCompletedLineLength = 0;
    currentLineLength = 0;
};

MeasureLineStringMode.onSetup = function (opts) {
    opts = opts || {};
    const featureId = opts.featureId;
    isMeters = store.state.isMeters;

    let line, currentVertexPosition, collection;
    let direction = 'forward';
    if (featureId) {
        line = this.getFeature(featureId);
        if (!line) {
            throw new Error(
                'Could not find a feature with the provided featureId',
            );
        }
        let from = opts.from;
        if (
            from &&
            from.type === 'Feature' &&
            from.geometry &&
            from.geometry.type === 'Point'
        ) {
            from = from.geometry;
        }
        if (
            from &&
            from.type === 'Point' &&
            from.coordinates &&
            from.coordinates.length === 2
        ) {
            from = from.coordinates;
        }
        if (!from || !Array.isArray(from)) {
            throw new Error(
                'Please use the `from` property to indicate which point to continue the line from',
            );
        }
        const lastCoord = line.coordinates.length - 1;
        if (
            line.coordinates[lastCoord][0] === from[0] &&
            line.coordinates[lastCoord][1] === from[1]
        ) {
            currentVertexPosition = lastCoord + 1;
            // add one new coordinate to continue from
            line.addCoordinate(
                currentVertexPosition,
                ...line.coordinates[lastCoord],
            );
        } else if (
            line.coordinates[0][0] === from[0] &&
            line.coordinates[0][1] === from[1]
        ) {
            direction = 'backwards';
            currentVertexPosition = 0;
            // add one new coordinate to continue from
            line.addCoordinate(currentVertexPosition, ...line.coordinates[0]);
        } else {
            throw new Error(
                '`from` should match the point at either the start or the end of the provided LineString',
            );
        }
    } else {
        line = this.newFeature({
            type: Constants.geojsonTypes.FEATURE,
            properties: {
                totalMeasurementLabel: 'origin',
            },
            geometry: {
                type: Constants.geojsonTypes.LINE_STRING,
                coordinates: [],
            },
        });

        collection = {
            type: Constants.geojsonTypes.FEATURE_COLLECTION,
            features: [line],
        };

        currentVertexPosition = 0;

        this.addFeature(line);
    }

    this.clearSelectedFeatures();
    doubleClickZoom.disable(this);
    this.updateUIClasses({ mouse: Constants.cursors.ADD });
    this.activateUIButton(Constants.types.LINE);
    this.setActionableState({
        trash: true,
    });

    return {
        line,
        currentVertexPosition,
        direction,
        collection,
    };
};

const createVertex = (
    parentId,
    coordinates,
    path,
    selected,
    totalMeasurement,
) => {
    return {
        type: 'Feature',
        properties: {
            meta: 'vertex',
            parent: parentId,
            coord_path: path,
            active: selected ? 'true' : 'false',
            totalMeasurement,
        },
        geometry: {
            type: 'Point',
            coordinates,
        },
    };
};

const pushMeasurement = () => {
    if (measurement.metricTotal === 0) return;

    measurement.metricMeasurement.push(
        measurement.metricTotal - previousMeasureTotal,
    );
    lastCompletedLineLength = measurement.metricTotal - previousMeasureTotal;
    previousMeasureTotal = measurement.metricTotal;
};

const calculateDistance = (v1: number[], v2: number[]): number => {
    const dx = v2[0] - v1[0];
    const dy = v2[1] - v1[1];
    return Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2));
};

/**
 * @param coords - An array with 2 elements which represent lon,lat.
 */
const convertCoords = (coords: number[]) => {
    const utmZone = Math.ceil((coords[0] + 180) / 6);

    let epsgStart = 326; // 326 is for north
    if (coords[1] < 0) {
        epsgStart = 327; // 327 is for south
    }

    const epsg = `EPSG:${epsgStart}${utmZone}`;

    return {
        epsg: epsg,
        coordinates: proj4('EPSG:4326', epsg, coords),
    };
};

const getDisplayMeasurements = (feature, state) => {
    for (let i = 0; i < feature.geometry.coordinates.length - 1; i++) {
        let v1 = convertCoords(feature.geometry.coordinates[i]);
        let v2 = convertCoords(feature.geometry.coordinates[i + 1]);

        if (v1.epsg !== v2.epsg) {
            let drawnLength = lineDistance(feature) * 1000;

            measurement.metricTotal = drawnLength + previousMeasureTotal;

            currentLineLength = drawnLength;

            break;
        } else {
            currentLineLength = calculateDistance(
                v1.coordinates,
                v2.coordinates,
            );

            measurement.metricTotal = currentLineLength + previousMeasureTotal;
        }
        state.line.properties.measurement = measurement.metricTotal;
    }
};

MeasureLineStringMode.clickAnywhere = function (state, e, isMobile) {
    if (
        (state.currentVertexPosition > 0 &&
            isEventAtCoordinates(
                e,
                state.line.coordinates[state.currentVertexPosition - 1],
            )) ||
        (state.direction === 'backwards' &&
            isEventAtCoordinates(
                e,
                state.line.coordinates[state.currentVertexPosition + 1],
            ))
    ) {
        state.collection.features.forEach((feature, i) => {
            if (!feature.geometry) return;

            let newLine = this.newFeature({
                type: Constants.geojsonTypes.FEATURE,
                properties: {
                    active: feature.properties.active,
                    id: `feature.properties.id ${i}`,
                    measurement: feature.properties.measurement,
                    measurementLabel: `${
                        Math.round(feature.properties.measurement * 100) / 100
                    } m`,
                    meta: feature.properties.meta,
                    'meta:type': 'LineString',
                    mode: feature.properties.mode,
                    measurementTotal: measurement.metricTotal,
                },
                geometry: {
                    type: Constants.geojsonTypes.LINE_STRING,
                    coordinates: feature.geometry.coordinates,
                },
            });

            this.addFeature(newLine);
        });

        resetLineMeasurements();
    }

    if (!isMobile) {
        pushMeasurement();
    }

    if (state.currentVertexPosition > 0) {
        const pushFeature = {
            type: Constants.geojsonTypes.FEATURE,
            properties: {
                id: `string ${state.line.currentVertexPosition}`,
                meta: 'feature',
                'meta:type': 'LineString',
                active: 'true',
                mode: 'draw_line_string',
                measurementLabel: `${
                    Math.round(
                        lastCompletedLineLength *
                            100 *
                            (isMeters ? 1 : 1.09361),
                    ) / 100
                } ${isMeters ? 'm' : 'yd'}`,
                measurement: lastCompletedLineLength,
            },
            geometry: {
                type: Constants.geojsonTypes.LINE_STRING,
                coordinates: [
                    state.line.coordinates[state.currentVertexPosition - 1],
                    state.line.coordinates[state.currentVertexPosition],
                ],
            },
        };

        if (isMobile) {
            let mobileFeature = {
                type: Constants.geojsonTypes.FEATURE,
                properties: {
                    id: `string ${state.line.currentVertexPosition}`,
                    meta: 'feature',
                    'meta:type': 'LineString',
                    active: 'true',
                    mode: 'draw_line_string',
                    measurementLabel: `${
                        Math.round(
                            lastCompletedLineLength *
                                100 *
                                (isMeters ? 1 : 1.09361),
                        ) / 100
                    } ${isMeters ? 'm' : 'yd'}`,
                    measurement: lastCompletedLineLength,
                },
                geometry: {
                    type: Constants.geojsonTypes.LINE_STRING,
                    coordinates: [
                        state.line.coordinates[state.currentVertexPosition],
                        [e.lngLat.lng, e.lngLat.lat],
                    ],
                },
            };
            getDisplayMeasurements(mobileFeature, state);
            pushMeasurement();

            mobileFeature = {
                type: Constants.geojsonTypes.FEATURE,
                properties: {
                    id: `string ${state.line.currentVertexPosition}`,
                    meta: 'feature',
                    'meta:type': 'LineString',
                    active: 'true',
                    mode: 'draw_line_string',
                    measurementLabel: `${
                        Math.round(
                            lastCompletedLineLength *
                                100 *
                                (isMeters ? 1 : 1.09361),
                        ) / 100
                    } ${isMeters ? 'm' : 'yd'}`,
                    measurement: lastCompletedLineLength,
                },
                geometry: {
                    type: Constants.geojsonTypes.LINE_STRING,
                    coordinates: [
                        state.line.coordinates[state.currentVertexPosition],
                        [e.lngLat.lng, e.lngLat.lat],
                    ],
                },
            };

            state.collection.features.push(mobileFeature);
        } else {
            state.collection.features.push(pushFeature);
        }
    }

    this.updateUIClasses({ mouse: Constants.cursors.ADD });

    state.line.updateCoordinate(
        state.currentVertexPosition,
        e.lngLat.lng,
        e.lngLat.lat,
    );

    if (state.direction === 'forward') {
        state.currentVertexPosition++;
        state.line.updateCoordinate(
            state.currentVertexPosition,
            e.lngLat.lng,
            e.lngLat.lat,
        );
    } else {
        state.line.addCoordinate(0, e.lngLat.lng, e.lngLat.lat);
    }
};

MeasureLineStringMode.clickOnVertex = function (state) {
    state.collection.features.forEach((feature, i) => {
        if (!feature.geometry) return;

        let newLine = this.newFeature({
            type: Constants.geojsonTypes.FEATURE,
            properties: {
                active: feature.properties.active,
                id: `feature.properties.id ${i}`,
                measurement: feature.properties.measurement,
                measurementLabel: feature.properties.measurementLabel,
                meta: feature.properties.meta,
                'meta:type': 'LineString',
                mode: feature.properties.mode,
                measurementTotal: measurement.metricTotal,
            },
            geometry: {
                type: Constants.geojsonTypes.LINE_STRING,
                coordinates: feature.geometry.coordinates,
            },
        });

        this.addFeature(newLine);
    });

    resetLineMeasurements();
    this.changeMode('MeasureLineStringMode');
};

MeasureLineStringMode.onMouseMove = function (state, e) {
    state.line.updateCoordinate(
        state.currentVertexPosition,
        e.lngLat.lng,
        e.lngLat.lat,
    );
    if (CommonSelectors.isVertex(e)) {
        this.updateUIClasses({ mouse: Constants.cursors.POINTER });
    }
};

MeasureLineStringMode.onTap = function (state, e) {
    tempState = state;
    //if (CommonSelectors.isVertex(e)) return this.clickOnVertex(state, e);
    this.clickAnywhere(state, e, true);
};

MeasureLineStringMode.onClick = function (state, e) {
    tempState = state;
    if (CommonSelectors.isVertex(e)) return this.clickOnVertex(state, e);
    this.clickAnywhere(state, e, false);
};

MeasureLineStringMode.onKeyUp = function (state, e) {
    const isEnterKey = CommonSelectors.isEnterKey(e);
    const isEscapeKey = CommonSelectors.isEscapeKey(e);

    if (!isEnterKey && !isEscapeKey) return;

    if (isEnterKey) return this.changeMode('MeasureLineStringMode');

    this.deleteFeature([state.line.id], { silent: true });
    this.changeMode('MeasureLineStringMode');
};

MeasureLineStringMode.onStop = function (state) {
    doubleClickZoom.enable(this);
    this.activateUIButton();

    // check to see if we've deleted this feature
    if (this.getFeature(state.line.id) === undefined) return;

    //remove last added coordinate
    state.line.removeCoordinate(`${state.currentVertexPosition}`);
    if (state.line.isValid()) {
        this.map.fire(Constants.events.CREATE, {
            features: [state.line.toGeoJSON()],
        });
    } else {
        this.deleteFeature([state.line.id], { silent: true });
        resetLineMeasurements();
    }
};

MeasureLineStringMode.onTrash = function (state) {
    this.deleteFeature([state.line.id], { silent: true });
    resetLineMeasurements();
};

MeasureLineStringMode.toDisplayFeatures = function (state, geojson, display) {
    const isActiveLine = geojson.properties.id === state.line.id;
    geojson.properties.active = isActiveLine
        ? Constants.activeStates.ACTIVE
        : Constants.activeStates.INACTIVE;
    if (!isActiveLine) return display(geojson);
    // Only render the line if it has at least one real coordinate
    if (geojson.geometry.coordinates.length < 2) return;
    geojson.properties.meta = Constants.meta.FEATURE;

    getDisplayMeasurements(geojson, state);

    display(
        createVertex(
            state.line.id,
            geojson.geometry.coordinates[
                state.direction === 'forward'
                    ? geojson.geometry.coordinates.length - 2
                    : 1
            ],
            `${
                state.direction === 'forward'
                    ? geojson.geometry.coordinates.length - 2
                    : 1
            }`,
            false,
            `${
                Math.round(
                    measurement.metricTotal * 100 * (isMeters ? 1 : 1.09361),
                ) / 100
            } ${isMeters ? 'm' : 'yd'}`,
        ),
    );

    if (state.collection.features[1]) {
        state.collection.features.forEach((feature, i) => {
            if (i === 0) return;
            display(feature);
        });
    }

    // create custom feature for the current pointer position
    const currentVertex = {
        type: 'Feature',
        properties: {
            meta: 'currentPosition',
            parent: state.line.id,
            totalMeasurementLabel: `${
                Math.round(
                    measurement.metricTotal * 100 * (isMeters ? 1 : 1.09361),
                ) / 100
            } ${isMeters ? 'm' : 'yd'}`,
            totalMeasurement:
                measurement.metricTotal * (isMeters ? 1 : 1.09361),
        },
        geometry: {
            type: 'Point',
            coordinates:
                geojson.geometry.coordinates[
                    geojson.geometry.coordinates.length - 1
                ],
        },
    };
    display(currentVertex);

    const currentLine = {
        type: 'Feature',
        properties: {
            meta: 'currentLine',
            parent: state.line.id,
            measurementLabel: `${
                Math.round(currentLineLength * 100 * (isMeters ? 1 : 1.09361)) /
                100
            } ${isMeters ? 'm' : 'yd'}`,
            measurement: currentLineLength * (isMeters ? 1 : 1.09361),
        },
        geometry: {
            type: 'LineString',
            coordinates: [
                geojson.geometry.coordinates[
                    geojson.geometry.coordinates.length - 1
                ],
                geojson.geometry.coordinates[
                    geojson.geometry.coordinates.length - 2
                ],
            ],
        },
    };

    display(currentLine);
};

MeasureLineStringMode.onDrawFinished = function () {
    resetLineMeasurements();

    return tempState;
};

export default MeasureLineStringMode;
